import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { expensesAPI, normalizeCategoryList } from '../../data/expenses';
import { unwrap } from '../../data/api';
import { LIST_STALE_TIME, OVERVIEW_STALE_TIME } from '../../lib/queryDefaults';
import { expenseMatchesCycleFilters } from '../../utils/selfieCycles';
import { patchBudgetOverviewSpend } from '../../utils/budgetOverviewSpendPatch';
import { calendarKeys } from '../calendar/queries';
import { invalidateFinancialData } from '../financialCache';
import { invalidateGuidedFlow } from '../guidedFlow/queries';

export const expensesKeys = {
  all: ['expenses'],
  categories: () => [...expensesKeys.all, 'categories'],
  list: (filters) => [...expensesKeys.all, 'list', filters || {}],
  latest: (filters) => [...expensesKeys.all, 'latest', filters || {}],
  summary: (params) => [...expensesKeys.all, 'summary', params || {}],
  totals: (filters) => [...expensesKeys.all, 'totals', filters || {}],
  orphans: () => [...expensesKeys.categories(), 'orphans'],
  recurring: () => [...expensesKeys.all, 'recurring'],
  annual: () => [...expensesKeys.all, 'annual'],
  // CHANGE v2 §16: cache key para top categorias.
  topCategories: (params) => [...expensesKeys.categories(), 'top', params || {}],
  systemPot: () => [...expensesKeys.categories(), 'system-pot'],
};

function invalidateAnnualOccasionalQueries(qc) {
  qc.invalidateQueries({ queryKey: expensesKeys.annual() });
  qc.invalidateQueries({ queryKey: calendarKeys.all });
  invalidateGuidedFlow(qc);
  void qc.invalidateQueries({ queryKey: ['pots'], exact: false });
}


// ----- Categories ---------------------------------------------------------

export function useCategoriesQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: [...expensesKeys.categories(), params || {}],
    queryFn: () =>
      expensesAPI.listCategories(params).then((r) => {
        const raw = unwrap(r, []);
        return normalizeCategoryList(raw);
      }),
    ...queryOptions,
  });
}

/** Active (non-archived) categories — shares cache with unfiltered list via `select`. */
export function useActiveCategoriesQuery(queryOptions = {}) {
  return useCategoriesQuery(undefined, {
    ...queryOptions,
    select: (categories) =>
      Array.isArray(categories) ? categories.filter((c) => !c.is_archived) : [],
  });
}

export function useCreateCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      expensesAPI.createCategory(payload).then((r) => unwrap(r)),
    onSuccess: (created) => {
      if (!created?.id) return;
      qc.setQueriesData(
        { queryKey: expensesKeys.categories(), exact: false },
        (old) => {
          const list = normalizeCategoryList(old);
          if (list.some((c) => String(c.id) === String(created.id))) {
            return list;
          }
          return [...list, created];
        },
      );
      qc.setQueriesData(
        { queryKey: expensesKeys.topCategories(), exact: false },
        (old) => {
          const list = Array.isArray(old) ? old : [];
          if (list.some((c) => String(c.id) === String(created.id))) {
            return list;
          }
          return [created, ...list];
        },
      );
      void qc.invalidateQueries({ queryKey: expensesKeys.categories() });
      void qc.invalidateQueries({ queryKey: ['budgets', 'overview'], exact: false });
      invalidateGuidedFlow(qc);
    },
  });
}

export function useUpdateCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      expensesAPI.updateCategory(id, payload).then((r) => unwrap(r)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: expensesKeys.categories() });
      invalidateGuidedFlow(qc);
    },
  });
}

export function useArchiveCategoryMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => expensesAPI.archiveCategory(id).then((r) => unwrap(r)),
    onSuccess: () => qc.invalidateQueries({ queryKey: expensesKeys.categories() }),
  });
}

export function useOrphanCheckQuery({ enabled = true } = {}) {
  return useQuery({
    queryKey: expensesKeys.orphans(),
    queryFn: () => expensesAPI.orphanCheck().then((r) => unwrap(r, [])),
    enabled,
  });
}

// CHANGE v2 §16: top N categorias mais usadas (QuickExpenseSheet).
export function useTopCategoriesQuery(params, queryOptions = {}) {
  return useQuery({
    queryKey: expensesKeys.topCategories(params),
    queryFn: () => expensesAPI.topCategories(params).then((r) => unwrap(r, [])),
    staleTime: 60_000,
    ...queryOptions,
  });
}

export function useSystemPotCategoryQuery(queryOptions = {}) {
  return useQuery({
    queryKey: expensesKeys.systemPot(),
    queryFn: () => expensesAPI.getSystemPotCategory().then((r) => unwrap(r)),
    staleTime: 300_000,
    ...queryOptions,
  });
}

export function useReorderCategoriesMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) =>
      expensesAPI.reorderCategories(ids).then((r) => unwrap(r, [])),
    onSuccess: () => qc.invalidateQueries({ queryKey: expensesKeys.categories() }),
  });
}

// ----- Expenses -----------------------------------------------------------

export function useExpensesInfiniteQuery(filters, queryOptions = {}) {
  return useInfiniteQuery({
    queryKey: expensesKeys.list(filters),
    initialPageParam: null,
    queryFn: ({ pageParam }) => {
      const fetcher = pageParam
        ? expensesAPI.getExpensesPage(pageParam)
        : expensesAPI.listExpenses(filters);
      return fetcher.then((r) => unwrap(r));
    },
    getNextPageParam: (last) => last?.next || null,
    placeholderData: (prev) => prev,
    staleTime: LIST_STALE_TIME,
    ...queryOptions,
  });
}

async function invalidateExpenseCaches(qc, invalidateFilters) {
  if (invalidateFilters !== undefined && invalidateFilters !== null) {
    await qc.invalidateQueries({ queryKey: expensesKeys.list(invalidateFilters) });
  }
  await qc.invalidateQueries({ predicate: listPredicate });
  await invalidateFinancialData(qc);
}

const listPredicate = (query) =>
  query.queryKey[0] === 'expenses' && query.queryKey[1] === 'list';

function expenseMatchesListFilters(expense, filters) {
  if (filters?.occurred_on_from && filters?.occurred_on_to) {
    return expenseMatchesCycleFilters(expense, filters);
  }
  if (!filters?.year || !filters?.month || !expense?.occurred_on) {
    return true;
  }
  const [y, m] = expense.occurred_on.split('-').map(Number);
  return y === Number(filters.year) && m === Number(filters.month);
}

function patchExpenseInListCaches(qc, expenseId, updated) {
  const targetId = String(expenseId);
  qc.setQueriesData({ predicate: listPredicate }, (old, query) => {
    if (!old?.pages?.length) return old;
    const filters = query.queryKey[2] || {};
    const visible = expenseMatchesListFilters(updated, filters);

    return {
      ...old,
      pages: old.pages.map((page, pageIndex) => {
        if (!page) return page;
        const results = page.results ?? [];
        const without = results.filter(
          (expense) => expense?.id != null && String(expense.id) !== targetId,
        );

        if (!visible) {
          return { ...page, results: without };
        }

        const exists = results.some(
          (expense) => expense?.id != null && String(expense.id) === targetId,
        );
        if (exists) {
          return {
            ...page,
            results: results.map((expense) =>
              expense?.id != null && String(expense.id) === targetId
                ? { ...expense, ...updated }
                : expense,
            ),
          };
        }

        if (pageIndex === 0) {
          return { ...page, results: [updated, ...without] };
        }
        return { ...page, results: without };
      }),
    };
  });
}

function removeExpenseFromListCaches(qc, expenseId) {
  qc.setQueriesData({ predicate: listPredicate }, (old) => {
    if (!old?.pages?.length) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        results: (page.results ?? []).filter((expense) => expense.id !== expenseId),
      })),
    };
  });
}

function findExpenseInListCaches(qc, expenseId) {
  const targetId = String(expenseId);
  const entries = qc.getQueriesData({ predicate: listPredicate });
  for (const [, data] of entries) {
    const pages = data?.pages;
    if (!Array.isArray(pages)) continue;
    for (const page of pages) {
      const hit = (page?.results ?? []).find(
        (expense) => String(expense?.id) === targetId,
      );
      if (hit) return hit;
    }
  }
  return null;
}

export function useCreateExpenseMutation(invalidateFilters) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      expensesAPI.createExpense(payload).then((r) => unwrap(r)),
    onMutate: async (payload) => {
      await qc.cancelQueries({ queryKey: expensesKeys.all });
      await qc.cancelQueries({ queryKey: ['budgets', 'overview'], exact: false });
      const previousEntries = qc.getQueriesData({ predicate: listPredicate });
      const previousOverviews = patchBudgetOverviewSpend(qc, {
        categoryId: payload.category,
        amountDelta: Number(payload.amount),
      });
      const categoryRows =
        qc.getQueryData([...expensesKeys.categories(), {}]) ?? [];
      const category = Array.isArray(categoryRows)
        ? categoryRows.find((c) => c.id === payload.category)
        : null;
      const optimisticId = `optimistic-${Date.now()}`;
      const optimistic = {
        id: optimisticId,
        category: payload.category,
        category_name: category?.name ?? '',
        category_icon: category?.icon ?? '',
        category_color: category?.color ?? '',
        group_id: category?.group ?? null,
        amount: String(payload.amount),
        currency: 'EUR',
        occurred_on: payload.occurred_on,
        description: payload.description ?? '',
        notes: '',
        source: 'manual',
        template: null,
        external_transaction_id: '',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      };
      qc.setQueriesData({ predicate: listPredicate }, (old) => {
        if (!old?.pages?.length) return old;
        const first = old.pages[0];
        const results = first.results ?? [];
        return {
          ...old,
          pages: [
            { ...first, results: [optimistic, ...results] },
            ...old.pages.slice(1),
          ],
        };
      });
      return { previousEntries, previousOverviews };
    },
    onSuccess: (created) => {
      if (created?.id) {
        qc.setQueriesData({ predicate: listPredicate }, (old) => {
          if (!old?.pages?.length) return old;
          return {
            ...old,
            pages: old.pages.map((page, pageIndex) => {
              if (pageIndex !== 0) return page;
              const results = page.results ?? [];
              const withoutOptimistic = results.filter(
                (expense) => !String(expense?.id ?? '').startsWith('optimistic-'),
              );
              const withoutDuplicate = withoutOptimistic.filter(
                (expense) => String(expense?.id) !== String(created.id),
              );
              return {
                ...page,
                results: [created, ...withoutDuplicate],
              };
            }),
          };
        });
      }
    },
    onError: (_err, _variables, context) => {
      context?.previousEntries?.forEach(([key, data]) => {
        qc.setQueryData(key, data);
      });
      context?.previousOverviews?.forEach(([key, data]) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: async () => {
      await invalidateExpenseCaches(qc, invalidateFilters);
    },
  });
}

export function useUpdateExpenseMutation(invalidateFilters) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }) => {
      const updated = unwrap(await expensesAPI.updateExpense(id, payload));
      if (updated?.id) {
        try {
          patchExpenseInListCaches(qc, id, updated);
        } catch {
          // A gravação no servidor já foi bem-sucedida; não falhar a mutação.
        }
      }
      return updated;
    },
    onMutate: async ({ id, payload }) => {
      await qc.cancelQueries({ queryKey: ['budgets', 'overview'], exact: false });
      const previous = findExpenseInListCaches(qc, id);
      if (!previous) return { previousOverviews: [] };

      const oldCategory = previous.category;
      const oldAmount = Number(previous.amount);
      const newCategory = payload.category ?? oldCategory;
      const newAmount =
        payload.amount != null ? Number(payload.amount) : oldAmount;

      let previousOverviews = [];
      if (String(oldCategory) === String(newCategory)) {
        previousOverviews = patchBudgetOverviewSpend(qc, {
          categoryId: oldCategory,
          amountDelta: newAmount - oldAmount,
        });
      } else {
        previousOverviews = patchBudgetOverviewSpend(qc, {
          categoryId: oldCategory,
          amountDelta: -oldAmount,
        });
        patchBudgetOverviewSpend(qc, {
          categoryId: newCategory,
          amountDelta: newAmount,
        });
      }
      return { previousOverviews };
    },
    onError: (_err, _variables, context) => {
      context?.previousOverviews?.forEach(([key, data]) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: async () => {
      await invalidateExpenseCaches(qc, invalidateFilters);
    },
  });
}

export function useDeleteExpenseMutation(invalidateFilters) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      unwrap(await expensesAPI.deleteExpense(id));
      try {
        removeExpenseFromListCaches(qc, id);
      } catch {
        // Idem: apagar no servidor não deve falhar por causa da cache local.
      }
      return null;
    },
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: ['budgets', 'overview'], exact: false });
      const previous = findExpenseInListCaches(qc, id);
      const previousOverviews = previous
        ? patchBudgetOverviewSpend(qc, {
            categoryId: previous.category,
            amountDelta: -Number(previous.amount),
          })
        : [];
      return { previousOverviews };
    },
    onError: (_err, _variables, context) => {
      context?.previousOverviews?.forEach(([key, data]) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: async () => {
      await invalidateExpenseCaches(qc, invalidateFilters);
    },
  });
}

export function useExpenseTotalsQuery(filters, queryOptions = {}) {
  const { enabled = true, ...rest } = queryOptions;
  const canFetch = Boolean(
    filters?.occurred_on_from && filters?.occurred_on_to,
  );

  return useQuery({
    queryKey: expensesKeys.totals(filters),
    queryFn: () => expensesAPI.totals(filters).then((r) => unwrap(r)),
    enabled: enabled && canFetch,
    staleTime: LIST_STALE_TIME,
    placeholderData: (prev) => prev,
    ...rest,
  });
}

export function useExpenseSummaryQuery(params) {
  const { enabled = true, ...queryParams } = params || {};
  const canFetch =
    queryParams.period === 'selfie' ||
    Boolean(queryParams.occurred_on_from && queryParams.occurred_on_to) ||
    Boolean(queryParams.year && queryParams.month);

  return useQuery({
    queryKey: expensesKeys.summary(queryParams),
    queryFn: () => expensesAPI.summary(queryParams).then((r) => unwrap(r)),
    enabled: enabled && canFetch,
    staleTime: OVERVIEW_STALE_TIME,
    placeholderData: (prev) => prev,
  });
}

/** First page of expenses for the cycle (or calendar month) — dashboard top 5. */
export function useLatestExpensesQuery({
  year,
  month,
  occurred_on_from,
  occurred_on_to,
  enabled = true,
} = {}) {
  const useCycle = Boolean(occurred_on_from && occurred_on_to);
  const filters = useCycle
    ? { occurred_on_from, occurred_on_to }
    : { year, month };

  return useQuery({
    queryKey: expensesKeys.latest(filters),
    queryFn: () => expensesAPI.listExpenses(filters).then((r) => unwrap(r)),
    enabled:
      enabled &&
      (useCycle || Boolean(year && month)),
    staleTime: LIST_STALE_TIME,
    placeholderData: (prev) => prev,
    select: (page) => (page?.results ?? []).slice(0, 5),
  });
}

// ----- Recurring + Annual -------------------------------------------------

export function useRecurringTemplatesQuery() {
  return useQuery({
    queryKey: expensesKeys.recurring(),
    queryFn: () =>
      expensesAPI.listRecurringTemplates().then((r) => unwrap(r, [])),
  });
}

export function useCreateRecurringTemplateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      expensesAPI.createRecurringTemplate(payload).then((r) => unwrap(r)),
    onSuccess: () => qc.invalidateQueries({ queryKey: expensesKeys.recurring() }),
  });
}

export function useUpdateRecurringTemplateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      expensesAPI.updateRecurringTemplate(id, payload).then((r) => unwrap(r)),
    onSuccess: () => qc.invalidateQueries({ queryKey: expensesKeys.recurring() }),
  });
}

export function useDeleteRecurringTemplateMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      expensesAPI.deleteRecurringTemplate(id).then((r) => unwrap(r)),
    onSuccess: () => qc.invalidateQueries({ queryKey: expensesKeys.recurring() }),
  });
}

export function useLaunchRecurringMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => expensesAPI.launchRecurringNow(id).then((r) => unwrap(r)),
    onSuccess: async () => {
      await invalidateFinancialData(qc);
      qc.invalidateQueries({ queryKey: expensesKeys.recurring() });
    },
  });
}

export function useAnnualOccasionalsQuery() {
  return useQuery({
    queryKey: expensesKeys.annual(),
    queryFn: () =>
      expensesAPI.listAnnualOccasionals().then((r) => unwrap(r, [])),
  });
}

export function useCreateAnnualOccasionalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      expensesAPI.createAnnualOccasional(payload).then((r) => unwrap(r)),
    onSuccess: () => invalidateAnnualOccasionalQueries(qc),
  });
}

export function useUpdateAnnualOccasionalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) =>
      expensesAPI.updateAnnualOccasional(id, payload).then((r) => unwrap(r)),
    onSuccess: () => invalidateAnnualOccasionalQueries(qc),
  });
}

export function useDeleteAnnualOccasionalMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      expensesAPI.deleteAnnualOccasional(id).then((r) => unwrap(r)),
    onSuccess: () => invalidateAnnualOccasionalQueries(qc),
  });
}
